/*
 * Where Automations live. (Automations were Skills in Firm Knowledge; the
 * stored records and APIs keep the `skill` name.)
 *
 *   /automations                        the firm's automations
 *   /automations?scope=mine|explore     my automations, or the starters
 *   /automations?skill=<id>             one automation open (preview / builder)
 *   /automations?from_thread=<id>       the builder, seeded from a case thread
 *   /automations?skill=<id>&from_run=<run>  the builder, asked to improve it from one run
 *   /automations?skill=<id>&optimize=1  the builder, asked to tighten its instructions
 *   /automations?skill=<id>&view=runs|history  one automation on its Runs or History tab
 *   /automations?new=1                  a new, unsaved automation (the editor)
 *   /automations?new=1&starter=<id>     the same, prefilled from a starter
 *   /automations?new=1&duplicate=<id>   the same, an unsaved copy of an automation
 *
 * The retired `/firm-knowledge?tab=skills&...` URLs (and links saved inside
 * older skills) redirect here with the same query. No React or IO here so
 * the rules stay unit-testable.
 */
import { matchPath } from "react-router-dom";

export const AUTOMATIONS_PATH = "/automations";

/** The list's pill tabs: everyone's automations, mine, or the starters. */
export type SkillScope = "firm" | "mine" | "explore";

/** An automation page's tab; `overview` is the default and left out of the URL. */
export type SkillView = "overview" | "runs" | "history";

/** Which tab a search string asks for (`overview` by default). */
export const skillViewOf = (search: string): SkillView => {
  const view = new URLSearchParams(search).get("view");
  return view === "runs" || view === "history" ? view : "overview";
};

/** Which scope a search string asks for (`firm` by default). */
export const skillScopeOf = (search: string): SkillScope => {
  const scope = new URLSearchParams(search).get("scope");
  return scope === "mine" || scope === "explore" ? scope : "firm";
};

export const automationsPath = (options?: {
  /** The list's scope; `firm` is the default and left out. */
  scope?: SkillScope | null;
  skillId?: string | null;
  /** Seed the builder from a case thread ("Save as Automation"). */
  fromThread?: string | null;
  /** Ask the builder to improve `skillId` from one of its runs. */
  fromRun?: string | null;
  /** Ask the builder to tighten `skillId`'s instructions ("Optimize With Builder"). */
  optimize?: boolean;
  /** Open a new, unsaved automation. */
  newSkill?: boolean;
  /** Prefill the new automation from this starter (`STARTER_SKILLS` id). */
  starter?: string | null;
  /** Prefill the new automation as a copy of this one. */
  duplicate?: string | null;
  /** The automation's tab; `overview` is left out. */
  view?: SkillView | null;
}): string => {
  const params = new URLSearchParams();
  if (options?.scope && options.scope !== "firm")
    params.set("scope", options.scope);
  if (options?.skillId) params.set("skill", options.skillId);
  if (options?.view && options.view !== "overview" && options?.skillId)
    params.set("view", options.view);
  if (options?.fromThread) params.set("from_thread", options.fromThread);
  if (options?.fromRun && options?.skillId)
    params.set("from_run", options.fromRun);
  if (options?.optimize && options?.skillId) params.set("optimize", "1");
  if (options?.newSkill) {
    params.set("new", "1");
    if (options.starter) params.set("starter", options.starter);
    else if (options.duplicate) params.set("duplicate", options.duplicate);
  }
  const query = params.toString();
  return query ? `${AUTOMATIONS_PATH}?${query}` : AUTOMATIONS_PATH;
};

export const isAutomationsPath = (pathname: string): boolean =>
  matchPath({ path: AUTOMATIONS_PATH, end: false }, pathname) !== null;

/**
 * Where a retired `/firm-knowledge?tab=skills...` search string should land:
 * the same query on `/automations` without `tab`. `null` when the search is
 * not for the Skills tab.
 */
export const automationsRedirectTarget = (search: string): string | null => {
  const params = new URLSearchParams(search);
  if (params.get("tab") !== "skills") return null;
  params.delete("tab");
  const query = params.toString();
  return query ? `${AUTOMATIONS_PATH}?${query}` : AUTOMATIONS_PATH;
};
